import { APP, ADDRESS_LABELS } from '../config/app';

const DEFAULT_AREA = 'Piler';
const DEFAULT_CITY = 'Piler';
const DEFAULT_STATE = 'Andhra Pradesh';
const DEFAULT_PINCODE = APP.defaultPincode || '517214';

export const createEmptyAddress = (overrides = {}) => ({
  id: `addr_${Date.now()}`,
  type: 'home',
  label: 'Home',
  emoji: '🏠',
  houseNo: '',
  building: '',
  landmark: '',
  area: DEFAULT_AREA,
  city: DEFAULT_CITY,
  state: DEFAULT_STATE,
  pincode: DEFAULT_PINCODE,
  receiverName: '',
  receiverPhone: '',
  text: '',
  isDefault: false,
  lat: null,
  lng: null,
  ...overrides,
});

/** Migrate legacy single-line `text` addresses into structured fields */
export const normalizeAddress = (addr) => {
  if (!addr) return createEmptyAddress();
  if (addr.houseNo || addr.building) {
    return {
      ...createEmptyAddress({ id: addr.id, isDefault: addr.isDefault }),
      ...addr,
      area: addr.area || DEFAULT_AREA,
      city: addr.city || DEFAULT_CITY,
      state: addr.state || DEFAULT_STATE,
      pincode: addr.pincode || DEFAULT_PINCODE,
    };
  }
  return {
    ...createEmptyAddress({ id: addr.id, type: addr.type, label: addr.label, emoji: addr.emoji, isDefault: addr.isDefault }),
    building: addr.text || '',
    text: addr.text || '',
  };
};

export const formatAddressText = (addr) => {
  const a = normalizeAddress(addr);
  if (a.text && !a.houseNo && !a.building) return a.text.trim();

  const parts = [
    a.houseNo?.trim(),
    a.building?.trim(),
    a.landmark?.trim(),
    [a.area, a.city].filter(Boolean).join(', '),
    a.pincode ? `- ${a.pincode}` : '',
  ].filter(Boolean);

  let line = parts.join(', ').replace(/,\s*-/g, ' -');
  if (a.receiverName?.trim()) line += ` (${a.receiverName.trim()})`;
  if (a.receiverPhone?.trim()) line += ` · ${a.receiverPhone.trim()}`;
  return line.replace(/\s+/g, ' ').trim();
};

export const getAddressLines = (addr) => {
  const a = normalizeAddress(addr);
  if (a.text && !a.houseNo && !a.building) {
    return { line1: a.text, line2: null, meta: null };
  }
  const line1 = [a.houseNo, a.building].filter((s) => s?.trim()).join(', ');
  const line2 = [a.landmark, a.area].filter((s) => s?.trim()).join(' · ');
  const meta = [a.city, a.state, a.pincode].filter(Boolean).join(', ');
  return { line1, line2: line2 || null, meta };
};

export const validateAddress = (addr, messages) => {
  const a = normalizeAddress(addr);
  const errors = {};
  if (!a.houseNo?.trim()) errors.houseNo = messages.houseNoRequired;
  if (!a.building?.trim()) errors.building = messages.buildingRequired;
  if (!a.pincode?.trim()) errors.pincode = messages.pincodeRequired;
  else if (!/^\d{6}$/.test(a.pincode.trim())) errors.pincode = messages.pincodeInvalid;
  if (a.receiverPhone?.trim() && !/^\d{10}$/.test(a.receiverPhone.replace(/\D/g, ''))) {
    errors.receiverPhone = messages.receiverPhoneInvalid;
  }
  return errors;
};

export const prepareAddressForSave = (editing) => {
  const typeMeta = ADDRESS_LABELS.find((l) => l.id === editing.type) || ADDRESS_LABELS[2];
  const normalized = normalizeAddress(editing);
  const entry = {
    ...normalized,
    type: editing.type,
    label: typeMeta.label,
    emoji: typeMeta.emoji,
    houseNo: normalized.houseNo.trim(),
    building: normalized.building.trim(),
    landmark: normalized.landmark.trim(),
    area: normalized.area.trim() || DEFAULT_AREA,
    city: normalized.city.trim() || DEFAULT_CITY,
    state: normalized.state.trim() || DEFAULT_STATE,
    pincode: normalized.pincode.trim(),
    receiverName: normalized.receiverName.trim(),
    receiverPhone: normalized.receiverPhone.replace(/\D/g, '').slice(-10),
    isDefault: !!editing.isDefault,
  };
  if (Number.isFinite(Number(editing.lat)) && Number.isFinite(Number(editing.lng))) {
    entry.lat = Number(editing.lat);
    entry.lng = Number(editing.lng);
  }
  entry.text = formatAddressText(entry);
  return entry;
};

export const hasGeoCoords = (addr) => {
  const a = normalizeAddress(addr);
  return Number.isFinite(a.lat) && Number.isFinite(a.lng);
};

export const mapsDirectionsUrl = (lat, lng) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
